import { useRef, useState } from "react";
import { useKeenSlider } from "keen-slider/react";
import {
  MotionBox as Box,
  MotionFlex,
  appearBelow,
  staggeredAppear,
} from "utils/motion";
import Image from "components/base/Image";
import Arrow from "components/icons/SliderThumbArrow";
import Text from "components/base/Text";
import Container from "components/base/Container";
import "keen-slider/keen-slider.min.css";

export default function SliderWithThumbsSection({
  title,
  description,
  images,
  captions,
  animate,
  inverted,
  ...props
}) {
  const ref = useRef();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [thumbnailRef, thumbSlider] = useKeenSlider({
    mode: "free-snap",
    initial: 0,
    slides: { perView: "auto", spacing: 18 },
    loop: false,
  });
  const [sliderRef, slider] = useKeenSlider({
    mode: "free-snap",
    slides: { origin: "center", perView: 1, spacing: 18 },
    loop: false,
    breakpoints: {
      "(min-width: 960px)": {
        mode: "free-snap",
        slides: { perView: 1.1, origin: "center", spacing: 18 },
      },
    },
    slideChanged(s) {
      setCurrentSlide(s.track.details.rel);
      thumbSlider.current?.moveToIdx(s.track.details.rel);
    },
  });

  if (!images?.length) {
    return null;
  }

  return (
    <MotionFlex
      ref={ref}
      bg={inverted ? "black.900" : "white"}
      position="relative"
      initial={animate ? "hidden" : "visible"}
      variants={staggeredAppear}
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      width="100%"
      pt="xxl"
      pb="xl"
      flexDirection="column"
      justifyContent="space-between"
      {...props}
      title={null}
    >
      {(title || description) && (
        <Container pb="xxxl" pt="xxl">
          <MotionFlex
            variants={appearBelow}
            direction="column"
            w={["90%", null, "75%", "800px"]}
          >
            {title && <Text type="headline_md">{title}</Text>}
            {description && (
              <Text type="p_lg" pt="xs">
                {description}
              </Text>
            )}
          </MotionFlex>
        </Container>
      )}

      <Box w="100%" h="100%" style={{ cursor: "grab" }} position="relative">
        <div className="navigation-wrapper">
          <div ref={sliderRef} className="keen-slider">
            {images.map((image) => (
              <Box w="100%" h="100%" className="keen-slider__slide" key={image}>
                <Image
                  src={image}
                  bg="black.700"
                  objectPosition="center"
                  objectFit="cover"
                  minHeight={["50vh", null, "75vh"]}
                  priority
                />
              </Box>
            ))}
          </div>
        </div>
        {slider && (
          <>
            <Arrow
              left
              currentSlide={currentSlide}
              onClick={(e) => e.stopPropagation() || slider.current?.prev()}
              disable={currentSlide === 0}
            />
            <Arrow
              right
              onClick={(e) => e.stopPropagation() || slider.current?.next()}
              disable={currentSlide === images.length - 1}
            />
          </>
        )}
      </Box>
      {captions && (
        <Box pt="sm" w="100%" px="md">
          <Text type="p_sm" fontWeight="bold" textAlign="center">
            {captions[currentSlide]}
          </Text>
        </Box>
      )}
      <MotionFlex mb="xl" mt={captions ? "lg" : "xl"} position="relative">
        <MotionFlex
          px={["lg", null, null, "xl"]}
          w="100%"
          h="100%"
          display={["none", "flex"]}
        >
          {slider && (
            <div ref={thumbnailRef} className="keen-slider thumbnail">
              {images.map((image, idx) => (
                <Box
                  w="100%"
                  h="100%"
                  className="keen-slider__slide"
                  style={{ maxWidth: 140, minWidth: 140, cursor: "pointer" }}
                  key={image}
                >
                  <Box
                    onClick={() => slider.current?.moveToIdx(idx)}
                    position="relative"
                  >
                    <Image
                      src={image}
                      bg="black.700"
                      objectPosition="center"
                      objectFit="cover"
                      minWidth="140px"
                      minHeight="80px"
                      priority
                    />
                    <Box
                      h="4px"
                      w="100%"
                      position="absolute"
                      bottom="0"
                      style={{ transition: "0.3s ease" }}
                      bg={currentSlide === idx ? "primary" : "transparent"}
                    />
                  </Box>
                </Box>
              ))}
            </div>
          )}
        </MotionFlex>
        <MotionFlex
          display={["flex", "none"]}
          justify="center"
          align="center"
          w="100%"
          h="100%"
        >
          <Text type="p_sm" fontWeight="bold" textAlign="center" color="white">
            {currentSlide + 1} / {images.length}
          </Text>
        </MotionFlex>
      </MotionFlex>
    </MotionFlex>
  );
}
